import { useState, useContext, useEffect } from 'react';
import { useRewardsState } from '../../../coordinators/rewards/hooks/useRewardsState';
import { RewardsCoordinatorContext } from '../../../coordinators/rewards/RewardsCoordinatorContext';
import type { RewardClaim } from '../../../services/rewards/types';
import { DiscordSupportLink } from './RewardsErrorStates';
import { CodeReveal } from './CodeReveal';
import { useShellToast } from './Toast';

interface RedemptionHistoryProps {
  onBack: () => void;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function ClaimDetail({ claim, onBack }: { claim: RewardClaim; onBack: () => void }) {
  const showToast = useShellToast();
  const isFailed = claim.status === 'failed' || claim.status === 'refunded';

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      {/* Back row */}
      <button type='button' onClick={onBack}
        className='mb-3 shrink-0 self-start border-none bg-transparent p-0 text-sm font-semibold text-[#FFFFFF]/90 transition-opacity hover:opacity-80'>
        &larr; Redemption history
      </button>
      <p className='shrink-0 text-[18px] font-semibold text-white' style={{ marginBottom: 12 }}>Reward details</p>

      {/* Scrollable content */}
      <div className='hr-scroll min-h-0 flex-1 overflow-y-auto' style={{ paddingRight: 4 }}>
        <div className='flex flex-col gap-3 pb-2'>
          {/* Art — uses hero gradient background, same size as hub */}
          <div className='relative mx-auto w-full shrink-0' style={{ maxWidth: 240 }}>
            <img src='/playcast-hero-gradient.svg' alt='' className='block w-full' draggable={false} />
            {claim.imageUrl && (
              <img src={claim.imageUrl} alt={claim.skuName} className='absolute inset-0 m-auto h-[75%] w-[75%] object-contain' />
            )}
          </div>

          <div>
            <div className='flex items-center gap-2'>
              <p className='text-base font-semibold text-white'>{claim.skuName || claim.skuId}</p>
              {isFailed && (
                <span className='rounded-full bg-[#FCA5A5]/15 px-2 py-0.5 text-[10px] font-medium text-[#FCA5A5]'>
                  {claim.status === 'failed' ? 'Failed' : 'Refunded'}
                </span>
              )}
            </div>
            <p className='mt-1 text-sm text-[#FFFFFF]/70'>Redeemed {formatDate(claim.createdAt)}</p>
          </div>

          {claim.code && (
            <CodeReveal code={claim.code} onCopied={() => showToast('Code copied')} />
          )}

          {claim.status === 'failed' && claim.failureReason && (
            <div className='rounded-lg border border-[#FCA5A5]/20 bg-[#FCA5A5]/5 px-3 py-2'>
              <p className='text-[10px] text-[#FFFFFF]/70'>Failure reason</p>
              <p className='mt-0.5 text-sm text-[#FCA5A5]'>{claim.failureReason}</p>
            </div>
          )}

          {claim.status === 'refunded' && claim.failureReason && (
            <div className='rounded-lg border border-[#FCA5A5]/20 bg-[#FCA5A5]/5 px-3 py-2'>
              <p className='text-[10px] text-[#FFFFFF]/70'>Refund reason</p>
              <p className='mt-0.5 text-sm text-[#FCA5A5]'>{claim.failureReason}</p>
            </div>
          )}

          {isFailed && (
            <div className='pt-1'>
              <DiscordSupportLink />
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

export const RedemptionHistory = ({ onBack }: RedemptionHistoryProps) => {
  const coordinator = useContext(RewardsCoordinatorContext);
  const { claims: coordinatorClaims } = useRewardsState();
  const [selectedClaim, setSelectedClaim] = useState<RewardClaim | null>(null);

  useEffect(() => {
    coordinator?.refreshClaims();
  }, [coordinator]);

  if (selectedClaim) {
    return <ClaimDetail claim={selectedClaim} onBack={() => setSelectedClaim(null)} />;
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      {/* Back row */}
      <button type='button' onClick={onBack}
        className='mb-3 shrink-0 self-start border-none bg-transparent p-0 text-sm font-semibold text-[#FFFFFF]/90 transition-opacity hover:opacity-80'>
        &larr; Settings
      </button>
      <p className='shrink-0 text-[18px] font-semibold text-white' style={{ marginBottom: 12 }}>Redemption history</p>

      {coordinatorClaims.length === 0 ? (
        <div className='flex flex-1 flex-col items-center justify-center gap-[10px] p-4'>
          <p className='font-semibold text-white'>No redemptions yet</p>
          <p className='text-center text-sm text-[#FFFFFF]/90'>
            When you redeem a gift card, it will show up here.
          </p>
        </div>
      ) : (
        <div className='hr-scroll min-h-0 flex-1 overflow-y-auto' style={{ paddingRight: 4 }}>
          <div className='flex flex-col gap-2 pb-2'>
            {coordinatorClaims.map((claim) => {
              const isFailed = claim.status === 'failed' || claim.status === 'refunded';
              return (
                <button
                  type='button'
                  key={claim.claimId}
                  onClick={() => setSelectedClaim(claim)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.08)',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div className='flex items-center gap-2'>
                    <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                      <div className='flex items-center gap-2'>
                        <span className='truncate text-sm font-semibold text-white'>{claim.skuName || claim.skuId}</span>
                        {isFailed && (
                          <span className='shrink-0 rounded-full bg-[#FCA5A5]/15 px-2 py-0.5 text-[10px] font-medium text-[#FCA5A5]'>
                            {claim.status === 'failed' ? 'Failed' : 'Refunded'}
                          </span>
                        )}
                      </div>
                      <span className='text-[10px] text-[#FFFFFF]/70'>{formatDate(claim.createdAt)}</span>
                    </div>
                    <span className='shrink-0 text-[11px] text-[#FFFFFF]/70'>Details &rarr;</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
